import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const BAGS_IMG = "https://images.unsplash.com/photo-1569512850669-7baa648e82d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHx0cmF2ZWwlMjBsdWdnYWdlJTIwYmFncyUyMGFpcnBvcnR8ZW58MHx8fHwxNzg3NTYwMTU1fDA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({ company_name: "EZShip Corporate", logo_url: "/ezship-logo.webp" });

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!identifier.trim() || !password) {
      setError("Please enter your email/username and password.");
      return;
    }
    setLoading(true);
    try {
      await login(identifier.trim(), password, remember);
      toast.success("Welcome back!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white" data-testid="login-page">
      {/* Brand panel */}
      <div className="relative hidden md:block">
        <img src={BAGS_IMG} alt="Passenger luggage ready for delivery" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#101E38]/80" />
        <div className="relative h-full flex flex-col justify-between p-10">
          <img src={settings.logo_url} alt="EZShip" className="h-12 w-auto bg-white rounded-sm px-3 py-2 self-start" data-testid="login-logo" />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#FF6B76] mb-3">Corporate Logistics Portal</div>
            <h1 className="text-4xl font-bold text-white tracking-tight leading-tight">
              Passenger baggage delivery,<br />managed end to end.
            </h1>
            <p className="mt-4 text-slate-300 text-sm max-w-md leading-relaxed">
              Track every order from pickup to delivery. One dashboard for admins, operations staff and client partners.
            </p>
          </div>
          <div className="text-xs text-slate-400">©2026 EZship India. All rights reserved.</div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="md:hidden flex justify-center mb-8">
            <img src={settings.logo_url} alt="EZShip" className="h-12 w-auto" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight" data-testid="login-heading">Sign in</h2>
          <p className="mt-2 text-sm text-slate-500">Use your email or username to access your dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-sm" data-testid="login-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="identifier" className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">Email or Username</Label>
              <Input
                id="identifier"
                data-testid="login-identifier-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@company.com"
                autoComplete="username"
                className="h-11 border-slate-300 focus:ring-2 focus:ring-primary rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="h-11 border-slate-300 focus:ring-2 focus:ring-primary rounded-sm pr-10"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} data-testid="toggle-password-visibility" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="remember" checked={remember} onCheckedChange={(v) => setRemember(Boolean(v))} data-testid="remember-me-checkbox" />
              <Label htmlFor="remember" className="text-sm text-slate-600 font-normal cursor-pointer">Remember me for 30 days</Label>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-primary hover:bg-[#152D53] text-white font-semibold rounded-sm transition-colors duration-150"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-slate-500" data-testid="login-assistance">
            For any assistance, mail us at{" "}
            <a href="mailto:care@ezship.in" className="font-semibold text-primary hover:underline">care@ezship.in</a>
          </p>
        </div>
      </div>
    </div>
  );
}
